// Simple circular detection, shouldn't be a problem in react-hook-form values.
// Hopefull 1_000 is deeper than we'll ever actually go...
const DEPTH_LIMIT = 1_000;

const isPlainObject = (thing: unknown): thing is Record<string, unknown> => Boolean(
  thing
    && typeof thing === 'object'
    && (
      thing.constructor === Object
      || thing.constructor === null
    ),
);

const areValuesEqualInternal = (referenceValue: unknown, checkValue: unknown, depth: number, type?: string): boolean => {
  if (referenceValue === checkValue) {
    return true;
  }

  if( type && type === 'currency') {
    // if type is currency, we only check the amount property
    if (
      typeof referenceValue === 'object'
      && typeof checkValue === 'object'
      && referenceValue !== null
      && checkValue !== null
    ) {
      if ('amount' in referenceValue ) {
        if ('amount' in checkValue) {
          return referenceValue.amount === checkValue.amount;
        }
        /** checkValue can sometimes not have an amount property (like means test data)
           * and when we click out value is set to zero so this handles where null was not equals zero
           * hence clicking out was showing currency as edited */
        return referenceValue.amount === 0;

      }
    }
    return false;
  }

  if (
    typeof referenceValue === 'object'
    && typeof checkValue === 'object'
    && referenceValue !== null
    && checkValue !== null
  ) {
    if ('amount' in referenceValue && 'amount' in checkValue) {
      if (referenceValue.amount === checkValue.amount) {
        return true;
      }
    }
  }

  if (
    // prevent infinite loops
    depth === DEPTH_LIMIT
    // if either value is null here, they are not equal. both null checked above
    || referenceValue === null
    || checkValue === null
    // if either value is undefined here, they are not equal. both undefined checked above
    || referenceValue === undefined
    || checkValue === undefined
  ) {
    return false;
  }

  const referenceIsArray = Array.isArray(referenceValue);
  const checkIsArray = Array.isArray(checkValue);
  if (referenceIsArray && checkIsArray) {
    if (referenceValue.length !== checkValue.length) {
      return false;
    }
    for (let i = 0; i < referenceValue.length; i += 1) {
      if (!areValuesEqualInternal(referenceValue[i], checkValue[i], depth + 1)) {
        return false;
      }
    }
    return true;
  }
  // if either value is an array, but the other is not, they are not equal
  if (referenceIsArray || checkIsArray) {
    return false;
  }

  if (isPlainObject(referenceValue) && isPlainObject(checkValue)) {
    // check all keys in the reference value are in the check
    // missing keys in the check are ok if the reference value is undefined
    const referenceKeys = Object.keys(referenceValue);
    for (let i = 0; i < referenceKeys.length; i += 1) {
      const key = referenceKeys[i];
      if (!areValuesEqualInternal(referenceValue[key], checkValue[key], depth + 1)) {
        return false;
      }
    }

    // check if there are any keys in checkValue (not undefined value), but not in referenceValue
    const checkKeys = Object.keys(checkValue);
    for (let i = 0; i < checkKeys.length; i += 1) {
      const key = checkKeys[i];
      if (checkValue[key] !== undefined && !(key in referenceValue)) {
        return false;
      }
    }

    return true;
  }

  return false;
};

const areValuesEqual = (referenceValue: unknown, checkValue: unknown, type?: string): boolean => {
  if (referenceValue === checkValue) {
    return true;
  }
  return areValuesEqualInternal(referenceValue, checkValue, 1, type);
};

export default areValuesEqual;